import React, { useState } from 'react';
import { useActiveClientsList } from '../../lib/hooks/useApi';
import { Client } from '../../lib/types/api';
import { ChevronDown, Search, X } from 'lucide-react';

interface ClientSelectorProps {
  value?: string;
  onChange: (clientId: string, client?: Client) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
}

export const ClientSelector: React.FC<ClientSelectorProps> = ({
  value,
  onChange,
  placeholder = "Select a client",
  disabled = false,
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: clients = [], isLoading, error: fetchError } = useActiveClientsList();

  const filteredClients = clients.filter(client =>
    client.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.spoc_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedClient = clients.find(client => client.id === value);

  const handleSelect = (client: Client) => {
    onChange(client.id, client);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    onChange('');
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative">
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full px-4 py-3 text-left glass-input rounded-xl backdrop-blur-xl border transition-all duration-200 ease-out focus:border-primary-500 focus:shadow-glow-primary focus:outline-none ${
            error
              ? 'border-error-500/50 bg-error-500/10'
              : 'border-glass-border/50 bg-glass-surface/50 hover:border-primary-500/30 hover:bg-glass-surface/70'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <div className="flex items-center justify-between">
            <span className={`${selectedClient ? 'text-text-enterprise-primary' : 'text-text-enterprise-tertiary'}`}>
              {selectedClient ? selectedClient.client_name : placeholder}
            </span>
            <div className="flex items-center space-x-2">
              {selectedClient && !disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                  className="text-text-enterprise-tertiary hover:text-text-enterprise-secondary"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              <ChevronDown className={`w-4 h-4 text-text-enterprise-tertiary transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>
          </div>
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-2 glass-card backdrop-blur-xl border-glass-border shadow-2xl rounded-xl">
            {/* Search */}
            <div className="p-4 border-b border-glass-border/30 bg-glass-surface/30 rounded-t-xl">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-enterprise-tertiary w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 text-sm glass-input rounded-lg focus:border-primary-500 focus:shadow-glow-primary focus:outline-none bg-glass-surface/50 border-glass-border/50"
                  autoFocus
                />
              </div>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="p-6 text-center text-text-enterprise-tertiary">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500 mx-auto"></div>
                <p className="mt-2 text-sm">Loading clients...</p>
              </div>
            )}

            {/* Error State */}
            {fetchError && (
              <div className="p-6 text-center text-error-500">
                <p className="text-sm">Error loading clients</p>
              </div>
            )}

            {/* Client List - No scrolling, show all clients */}
            {!isLoading && !fetchError && (
              <div className="max-h-80 overflow-y-auto">
                {filteredClients.length === 0 ? (
                  <div className="p-6 text-center text-text-enterprise-tertiary">
                    <p className="text-sm">
                      {searchTerm ? 'No clients found matching your search.' : 'No active clients available.'}
                    </p>
                  </div>
                ) : (
                  <div className="py-1">
                    {filteredClients.map((client) => (
                      <button
                        key={client.id}
                        type="button"
                        onClick={() => handleSelect(client)}
                        className={`w-full px-4 py-3 text-left focus:outline-none transition-all duration-200 ${
                          client.id === value 
                            ? 'bg-primary-500/20 text-primary-700 border-l-4 border-primary-500 font-medium' 
                            : 'text-text-enterprise-primary hover:bg-glass-surface/50 hover:text-primary-600'
                        }`}
                      >
                        <div className="flex flex-col">
                          <div className="font-medium text-base">{client.client_name}</div>
                          <div className="text-sm text-text-enterprise-tertiary mt-1">SPOC: {client.spoc_name}</div>
                          {client.contact_email && (
                            <div className="text-sm text-text-enterprise-tertiary">{client.contact_email}</div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-error-500">{error}</p>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};
